import { create } from 'zustand'
import dayjs from 'dayjs'
import {
  KeyDataSearch,
  DataSearchType,
  DATA_SEARCH_INIT,
  calcMaxNumberOfChildren,
  urlParamsUtils,
  ChildrenType,
} from '../components/search-bar/utils'

type ModalValidationErrors = {
  childBirthdate: boolean
  minStudyAbroad: boolean
}

type DateSelectTab = 'date' | 'month'

type TopPageStore = {
  isSearchModalOpen: boolean
  dataSearch: DataSearchType
  validationErrors: ModalValidationErrors
  dateSelectTab: DateSelectTab
  selectedStartDate?: Date

  initializeFromUrl: () => void
  openSearchModal: () => void
  closeSearchModal: () => void
  handleChangeDataSearch: (key: KeyDataSearch, value: number | string, type?: 'down' | 'up' | 'option' | 'text') => void
  handleClearForm: () => void
  handleChangeFromDate: (value: number | string | Date) => void
  handleStayDayRadioChange: (value: string) => void
  handleChangeDateSelectTab: (tab: DateSelectTab) => void
  handleBirthdayChange: (index: number, key: 'year' | 'month' | 'day', value: string) => void
  handleCounter: (key: KeyDataSearch, type: 'up' | 'down') => void
  handleSubmit: () => boolean
}

const INITIAL_VALIDATION_ERRORS: ModalValidationErrors = {
  childBirthdate: false,
  minStudyAbroad: false,
}

const normalizeDataSearch = (initDataSearch: DataSearchType): DataSearchType => {
  const updatedData = {
    ...DATA_SEARCH_INIT,
    ...initDataSearch,
    maximum_number_of_adults: initDataSearch.maximum_number_of_adults || 2,
  }
  const studyAbroadCount = Math.max(1, Math.min(20, updatedData.number_of_study_abroad || 1))
  const updatedBirthdays = [...(updatedData.birthdays || [])]
  updatedBirthdays.length = studyAbroadCount
  for (let i = 0; i < studyAbroadCount; i++) {
    updatedBirthdays[i] = updatedBirthdays[i] || { year: '', month: '', day: '' }
  }

  return {
    ...updatedData,
    birthdays: updatedBirthdays,
    maximum_number_of_children: calcMaxNumberOfChildren(
      updatedData.number_of_study_abroad,
      updatedData.number_of_preschoolers_1,
      updatedData.number_of_students_1,
    ),
  }
}

const validateForm = (value: DataSearchType) => {
  const validationResult = {
    isValid: true,
    errors: {
      childBirthdate: false,
      minStudyAbroad: false,
    },
  }

  if (value.number_of_study_abroad < 1) {
    validationResult.isValid = false
    validationResult.errors.minStudyAbroad = true
  }

  if (value.number_of_study_abroad > 0) {
    if (!value.birthdays || value.birthdays.length < value.number_of_study_abroad) {
      validationResult.isValid = false
      validationResult.errors.childBirthdate = true
    } else {
      const isAllChildBirthdaysFilled = value.birthdays.every((info) => info.year && info.month && info.day)
      if (!isAllChildBirthdaysFilled) {
        validationResult.isValid = false
        validationResult.errors.childBirthdate = true
      }
    }
  }

  return validationResult
}

const isDataSearchEmpty = (value: DataSearchType) => JSON.stringify(value) === JSON.stringify(DATA_SEARCH_INIT)

const handleGetTheNearestMonth = (): string[] => {
  const today = new Date()
  const year = today.getFullYear()
  const month = today.getMonth() + 1
  const result: string[] = []

  for (let i = 0; i < 3; i++) {
    const currentYear = year + Math.floor((month + i - 1) / 12)
    const currentMonth = ((month + i - 1) % 12) + 1
    result.push(`${currentYear}/${currentMonth.toString().padStart(2, '0')}`)
  }

  return result
}

const LINE_ACCEPT_CLASS_MAP: Record<string, string> = {
  '0': '0歳児',
  '1': '1歳児',
  '2': '2歳児',
  '3': '3歳児',
  '4': '4歳児',
  '5': '5歳児',
  '6': '1年生',
  '7': '2年生',
  '8': '3年生',
  '9': '4年生',
  '10': '5年生',
  '11': '6年生',
}

const parseAcceptClassValues = (rawValue: string): ChildrenType[] => {
  const values = rawValue
    .split(',', -1)
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
    .map((item) => LINE_ACCEPT_CLASS_MAP[item] || item)

  const uniqueValues = Array.from(new Set(values))
  return uniqueValues.map((value, index) => ({
    id: index + 1,
    name: value,
    value: value,
  }))
}

const generateSearchQueryURL = (currentKeySearch: string, data: DataSearchType) => {
  const currentQueryParams = new URLSearchParams(currentKeySearch)
  const simpleParams: Record<string, string> = {
    maximum_number_of_adults: String(data.maximum_number_of_adults),
    maximum_number_of_children: String(data.maximum_number_of_children),
    number_of_preschoolers_1: String(data.number_of_preschoolers_1),
    number_of_students_1: String(data.number_of_students_1),
    number_of_study_abroad: String(data.number_of_study_abroad),
    stay_day: data.stay_day?.join(',') || '',
    plan_month: data.plan_month?.join(',') || '',
    accept_class: data.accept_class?.map((item) => item.value).join(',') || '',
    start_date: data.start_date || '',
    end_date: data.end_date || '',
  }

  Object.keys(simpleParams).forEach((key) => {
    if (simpleParams[key] && !['0', 'false'].includes(simpleParams[key])) {
      currentQueryParams.set(key, simpleParams[key])
    } else {
      currentQueryParams.delete(key)
    }
  })

  const validBirthdays = data.birthdays?.filter((b) => b.year && b.month && b.day) || []
  if (validBirthdays.length > 0) {
    const birthdaysString = validBirthdays.map((b) => `${b.year}-${parseInt(b.month)}-${parseInt(b.day)}`).join(',')
    currentQueryParams.set('birthdays', birthdaysString)
  } else {
    currentQueryParams.delete('birthdays')
  }

  urlParamsUtils.resetPageNumber(currentQueryParams)
  return currentQueryParams.toString()
}

const syncSearchToUrl = (data: DataSearchType) => {
  const currentKeySearch = window.location.search
  const queryURL = generateSearchQueryURL(currentKeySearch, data)
  const params = new URLSearchParams(queryURL)
  urlParamsUtils.navigateWithParams(params)
}

export const useTopPageStore = create<TopPageStore>()((set, get) => ({
  isSearchModalOpen: false,
  dataSearch: DATA_SEARCH_INIT,
  validationErrors: INITIAL_VALIDATION_ERRORS,
  dateSelectTab: 'date',
  selectedStartDate: undefined,

  initializeFromUrl: () => {
    const queryString = window.location.search
    if (queryString.length > 0) {
      localStorage.setItem('top_url', queryString)
    } else {
      localStorage.removeItem('top_url')
    }

    const queryParams = new URLSearchParams(queryString)
    const tempObject: DataSearchType = { ...DATA_SEARCH_INIT }
    const arrayKeys = ['stay_day', 'plan_month']
    const booleanKeys = ['no_car']
    const numberKeys = ['maximum_number_of_adults', 'number_of_preschoolers_1', 'number_of_students_1', 'number_of_study_abroad']
    const keySearch = [
      'maximum_number_of_adults',
      'maximum_number_of_children',
      'number_of_preschoolers_1',
      'number_of_students_1',
      'number_of_study_abroad',
      'no_car',
      'stay_day',
      'plan_month',
      'accept_class',
      'birthdays',
      'start_date',
      'end_date',
    ]

    queryParams.forEach((value, key) => {
      if (keySearch.includes(key)) {
        if (arrayKeys.includes(key)) {
          if (key === 'plan_month' && value === 'recently') {
            tempObject[key] = handleGetTheNearestMonth()
          } else {
            tempObject[key] = value ? value.split(',') : []
          }
        } else if (key === 'accept_class') {
          tempObject.accept_class = parseAcceptClassValues(value)
        } else if (key === 'birthdays') {
          const arr = value ? value.split(',') : []
          tempObject.birthdays = arr.map((str) => {
            const [year, month, day] = str.split('-')
            return { year: year || '', month: month || '', day: day || '' }
          })
        } else if (booleanKeys.includes(key)) {
          tempObject[key] = value
        } else if (numberKeys.includes(key)) {
          tempObject[key] = Number(value)
        } else {
          tempObject[key] = value
        }
      }
    })

    set({
      dataSearch: normalizeDataSearch(tempObject),
      selectedStartDate: tempObject.start_date ? dayjs(tempObject.start_date).toDate() : undefined,
      validationErrors: INITIAL_VALIDATION_ERRORS,
      dateSelectTab: tempObject.plan_month?.length > 0 ? 'month' : 'date',
    })
  },

  openSearchModal: () => set({ isSearchModalOpen: true }),
  closeSearchModal: () => set({ isSearchModalOpen: false }),

  handleChangeDataSearch: (key, value, type) => {
    const dataSearch = get().dataSearch
    let newValue: number | string = value

    const handleRemoveChoseValueFromDataArray = (array: string[], removeValue: string) =>
      array.filter((item) => item !== removeValue)
    const handleAddChoseValueToDataArray = (array: string[], addValue: string) => [...array, addValue]

    switch (type) {
      case 'up':
      case 'down': {
        const increment = type === 'up' ? 1 : -1
        const potentialNewValue = Number(dataSearch[key]) + increment
        const minValue = key === 'maximum_number_of_adults' ? 1 : 0
        newValue = Math.max(minValue, Math.min(20, potentialNewValue))
        set((state) => ({ dataSearch: { ...state.dataSearch, [key]: newValue } }))
        return
      }
      case 'option': {
        if (key === 'stay_day') {
          const currentData = dataSearch[key]
          const isValueChosen = currentData?.includes(String(newValue))
          const updatedData = isValueChosen
            ? handleRemoveChoseValueFromDataArray(currentData, String(newValue))
            : handleAddChoseValueToDataArray(currentData, String(newValue))
          set((state) => ({ dataSearch: { ...state.dataSearch, [key]: updatedData } }))
          return
        }

        if (key === 'plan_month') {
          const currentPlanMonth = dataSearch[key]
          const isValueChosen = currentPlanMonth?.includes(String(newValue))
          const isValueChosenOverThree = currentPlanMonth?.length >= 3
          let updatedPlanMonth = []

          if (isValueChosen) {
            updatedPlanMonth = handleRemoveChoseValueFromDataArray(currentPlanMonth, String(newValue))
          } else if (isValueChosenOverThree) {
            return
          } else {
            updatedPlanMonth = handleAddChoseValueToDataArray(currentPlanMonth, String(newValue))
          }
          set((state) => ({ dataSearch: { ...state.dataSearch, [key]: updatedPlanMonth } }))
          return
        }
        break
      }
      case 'text': {
        const isValueChosen = dataSearch[key] === value
        newValue = isValueChosen ? '' : value
        break
      }
    }

    set((state) => ({ dataSearch: { ...state.dataSearch, [key]: newValue } }))
  },

  handleClearForm: () => {
    set({
      dataSearch: DATA_SEARCH_INIT,
      selectedStartDate: undefined,
      validationErrors: INITIAL_VALIDATION_ERRORS,
      dateSelectTab: 'date',
    })
  },

  handleChangeFromDate: (value) => {
    const selectedDate = value ? dayjs(value).toDate() : undefined
    const selectedStayDay = Number(get().dataSearch.stay_day?.[0] || 7)
    const selectedEndDate = selectedDate
      ? dayjs(selectedDate)
          .add(selectedStayDay - 1, 'day')
          .format('YYYY-MM-DD')
      : ''

    set((state) => ({
      selectedStartDate: selectedDate,
      dataSearch: {
        ...state.dataSearch,
        plan_month: [],
        start_date: selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : '',
        end_date: selectedEndDate,
      },
    }))
  },

  handleStayDayRadioChange: (value) => {
    const selectedStart = get().selectedStartDate ? dayjs(get().selectedStartDate) : null
    const updatedEndDate = selectedStart ? selectedStart.add(Number(value) - 1, 'day').format('YYYY-MM-DD') : ''

    set((state) => ({
      dataSearch: {
        ...state.dataSearch,
        stay_day: [value],
        end_date: updatedEndDate,
      },
    }))
  },

  handleChangeDateSelectTab: (tab) => {
    const dateSelectTab = get().dateSelectTab
    if (tab === dateSelectTab) return

    set((state) => {
      if (tab === 'date') {
        return {
          dateSelectTab: tab,
          dataSearch: {
            ...state.dataSearch,
            plan_month: [],
          },
        }
      }

      return {
        dateSelectTab: tab,
        selectedStartDate: undefined,
        dataSearch: {
          ...state.dataSearch,
          start_date: '',
          end_date: '',
        },
      }
    })
  },

  handleBirthdayChange: (index, key, value) => {
    set((state) => {
      const arr = state.dataSearch.birthdays ? [...state.dataSearch.birthdays] : []
      arr[index] = { ...arr[index], [key]: value }
      return {
        dataSearch: { ...state.dataSearch, birthdays: arr },
      }
    })
  },

  handleCounter: (key, type) => {
    const dataSearch = get().dataSearch
    if (typeof dataSearch[key] !== 'number') return

    let newValue = dataSearch[key] as number
    if (type === 'up') newValue = Math.min(newValue + 1, 20)
    if (type === 'down') {
      const minValue = key === 'maximum_number_of_adults' || key === 'number_of_study_abroad' ? 1 : 0
      newValue = Math.max(newValue - 1, minValue)
    }

    const updateDataSearch = { ...dataSearch, [key]: newValue }
    if (key === 'number_of_study_abroad') {
      const arr = [...(dataSearch.birthdays || [])]
      arr.length = newValue
      for (let i = 0; i < arr.length; i++) {
        arr[i] = arr[i] || { year: '', month: '', day: '' }
      }
      updateDataSearch.birthdays = arr
    }

    if (key === 'number_of_preschoolers_1' || key === 'number_of_students_1' || key === 'number_of_study_abroad') {
      updateDataSearch.maximum_number_of_children =
        (key === 'number_of_preschoolers_1' ? newValue : dataSearch.number_of_preschoolers_1) +
        (key === 'number_of_students_1' ? newValue : dataSearch.number_of_students_1) +
        (key === 'number_of_study_abroad' ? newValue : dataSearch.number_of_study_abroad)
    }

    set({ dataSearch: updateDataSearch })
  },

  handleSubmit: () => {
    const dataSearch = get().dataSearch
    const validationResult = validateForm(dataSearch)
    if (!validationResult.isValid) {
      set({
        validationErrors: {
          childBirthdate: validationResult.errors.childBirthdate,
          minStudyAbroad: validationResult.errors.minStudyAbroad,
        },
      })
      return false
    }

    set({ validationErrors: INITIAL_VALIDATION_ERRORS })
    if (isDataSearchEmpty(dataSearch)) {
      localStorage.removeItem('top_url')
    }
    syncSearchToUrl(dataSearch)
    set({ isSearchModalOpen: false })
    return true
  },
}))
